(() => {
  const gridSelector = '#ProductGridContainer[data-sakura-collection-grid]';
  const states = new WeakMap();
  const pageKeyPrefix = 'sakura:collection-pages:';
  const maximumAge = 30 * 60 * 1000;
  const navigationEntry = window.performance.getEntriesByType?.('navigation')?.[0];
  const isBackForwardNavigation = navigationEntry?.type === 'back_forward';

  function storageKey() {
    return `${pageKeyPrefix}${window.location.pathname}${window.location.search}`;
  }

  function readSavedPage() {
    try {
      const saved = JSON.parse(window.sessionStorage.getItem(storageKey()));
      if (!Number.isInteger(saved?.page) || saved.page < 2 || !Number.isFinite(saved.savedAt) || Date.now() - saved.savedAt > maximumAge) return 1;
      return saved.page;
    } catch (error) {
      return 1;
    }
  }

  function savePage(page) {
    try {
      window.sessionStorage.setItem(storageKey(), JSON.stringify({ page, savedAt: Date.now() }));
    } catch (error) {
      // The native pagination remains usable when session storage is unavailable.
    }
  }

  function setStatus(state, message) {
    if (state.message) state.message.textContent = message;
  }

  function setEnhanced(state, enabled) {
    if (enabled) {
      state.container.dataset.sakuraEnhanced = 'true';
      state.container.querySelector('[data-sakura-native-pagination]')?.setAttribute('hidden', '');
      if (state.button) state.button.hidden = false;
    } else {
      delete state.container.dataset.sakuraEnhanced;
      state.container.querySelector('[data-sakura-native-pagination]')?.removeAttribute('hidden');
      if (state.button) state.button.hidden = true;
    }
  }

  async function appendNext(state) {
    if (state.loading || !state.button?.dataset.nextUrl) return false;
    state.loading = true;
    state.button.disabled = true;
    state.button.setAttribute('aria-busy', 'true');
    state.container.setAttribute('aria-busy', 'true');
    setStatus(state, state.loadingMessage);

    try {
      const response = await fetch(state.button.dataset.nextUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = html.querySelector('#product-grid');
      if (!nextGrid) throw new Error('Product grid was not found');
      const currentGrid = state.container.querySelector('#product-grid');
      if (!currentGrid || currentGrid !== state.grid) {
        state.button.disabled = false;
        state.button.removeAttribute('aria-busy');
        return false;
      }

      nextGrid.querySelectorAll(':scope > li').forEach((item) => {
        if (item.matches('.scroll-trigger')) item.classList.add('scroll-trigger--cancel');
        item.querySelectorAll('.scroll-trigger').forEach((trigger) => trigger.classList.add('scroll-trigger--cancel'));
        currentGrid.append(item);
      });
      state.page += 1;
      savePage(state.page);
      const nextButton = html.querySelector('[data-sakura-load-more-button]');
      if (nextButton) {
        state.button.dataset.nextUrl = nextButton.dataset.nextUrl;
        state.button.disabled = false;
        state.button.removeAttribute('aria-busy');
        state.count.textContent = `${currentGrid.children.length} / ${state.total}`;
        state.button.textContent = nextButton.textContent;
        setStatus(state, '');
      } else {
        state.button.remove();
        state.button = null;
        state.count.textContent = `${currentGrid.children.length} / ${state.total}`;
        setStatus(state, '');
      }
      setEnhanced(state, true);
      return true;
    } catch (error) {
      if (state.container.querySelector('#product-grid') !== state.grid) {
        state.button.disabled = false;
        state.button.removeAttribute('aria-busy');
        return false;
      }
      setEnhanced(state, false);
      state.button.disabled = false;
      state.button.removeAttribute('aria-busy');
      setStatus(state, state.errorMessage);
      return false;
    } finally {
      state.loading = false;
      if (states.get(state.container) === state && state.container.querySelector('#product-grid') === state.grid) {
        state.container.removeAttribute('aria-busy');
      }
    }
  }

  function enhance(grid) {
    const container = grid.closest(gridSelector);
    if (!container) return;
    const button = container.querySelector('[data-sakura-load-more-button]');
    const root = container.querySelector('[data-sakura-load-more-root]');
    if (!button || !root || !grid) return;
    const previous = states.get(container);
    if (previous?.grid === grid && previous.button === button) return;

    const state = {
      container,
      grid,
      button,
      message: root.querySelector('[data-sakura-load-more-message]'),
      count: root.querySelector('[data-sakura-load-more-count]'),
      page: Number(grid.dataset.currentPage) || 1,
      total: Number(grid.dataset.totalItems) || grid.children.length,
      loadingMessage: root.querySelector('.sakura-collection-load-more__messages')?.dataset.loadingMessage,
      errorMessage: root.querySelector('.sakura-collection-load-more__messages')?.dataset.errorMessage,
      loading: false,
    };
    states.set(container, state);
    container.removeAttribute('aria-busy');
    button.hidden = false;
    button.addEventListener('click', () => appendNext(state));
    setEnhanced(state, true);

    const targetPage = Math.min(isBackForwardNavigation ? readSavedPage() : 1, Number(grid.dataset.totalPages) || 1);
    if (targetPage > state.page) {
      (async () => {
        while (state.page < targetPage && state.button) {
          if (!(await appendNext(state))) break;
        }
        document.documentElement.dataset.sakuraPagesRestored = 'true';
        document.dispatchEvent(new CustomEvent('sakura:collection:pages-restored'));
      })();
    } else {
      document.documentElement.dataset.sakuraPagesRestored = 'true';
    }
  }

  function scan() {
    document.querySelectorAll(`${gridSelector} #product-grid`).forEach(enhance);
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest?.('#product-grid a[href]')) {
      const grid = event.target.closest('#product-grid');
      const container = grid?.closest(gridSelector);
      const state = container && states.get(container);
      if (state) savePage(state.page);
    }
  }, true);
  window.addEventListener('pagehide', () => {
    document.querySelectorAll(gridSelector).forEach((container) => {
      const state = states.get(container);
      if (state) savePage(state.page);
    });
  });

  scan();
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
})();
